import { useParams, Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import SectionNav from "../components/SectionNav";
import { Stat, CtaBand, ImageBlock } from "../components/ui";
import { PROJECTS, FID_NOTE } from "../data/site";
import NotFound from "./NotFound";

function FactRow({ k, v }) {
    return (
        <div className="flex items-baseline justify-between gap-6 border-b border-line py-4">
            <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-3">{k}</dt>
            <dd className="text-right font-display text-base font-semibold text-ink">{v}</dd>
        </div>
    );
}

export default function ProjectDetail() {
    const { slug } = useParams();
    const p = PROJECTS.find((x) => x.slug === slug);
    if (!p) return <NotFound />;
    const others = PROJECTS.filter((x) => x.slug !== slug);

    const SECTIONS = [
        { id: "key-figures", label: "Key figures" },
        { id: "overview", label: "Overview" },
        { id: "more-projects", label: "Other projects" },
    ];

    return (
        <>
            <header className="bg-paper">
                <div className="container-x pb-14 pt-32 sm:pt-40 lg:pt-48">
                    <nav aria-label="Breadcrumb" className="mb-8" data-testid="breadcrumbs">
                        <ol className="flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-3">
                            <li><Link to="/" className="transition-colors hover:text-blue">Home</Link></li>
                            <li aria-hidden="true" className="text-line-2">/</li>
                            <li><Link to="/projects" className="transition-colors hover:text-blue">Projects</Link></li>
                            <li aria-hidden="true" className="text-line-2">/</li>
                            <li aria-current="page" className="text-ink-2">{p.name}</li>
                        </ol>
                    </nav>
                    <p className="eyebrow mb-6">
                        {p.market} — {p.tech}
                        {p.pci ? ` · ${p.pci}` : ""}
                    </p>
                    <h1 className="h1-display max-w-5xl">{p.name}</h1>
                    <p className="body-lg mt-6 max-w-2xl">{p.description}</p>
                </div>
            </header>

            <SectionNav items={SECTIONS} navKey={`project-${p.id}`} />

            <section id="key-figures" className="bg-navy text-white">
                <div className="container-x grid gap-10 py-16 sm:py-20 lg:grid-cols-3" data-testid="project-stats">
                    {p.stats.map((s, i) => (
                        <Reveal key={s.label} delay={i * 0.08}>
                            <Stat dark value={s.value} unit={s.unit} label={s.label} prefix={s.prefix} />
                        </Reveal>
                    ))}
                </div>
                <div className="border-t border-line-navy">
                    <p className="container-x py-4 font-mono text-[10px] uppercase tracking-[0.16em] text-white/45">
                        {FID_NOTE}
                    </p>
                </div>
            </section>

            <section id="overview" className="bg-paper">
                <div className="container-x grid gap-14 py-20 sm:py-24 lg:grid-cols-12 lg:py-28">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <dl data-testid="project-facts">
                                <FactRow k="Project location" v={p.location} />
                                <FactRow k="Technology" v={p.tech} />
                                <FactRow k="Status" v={p.status} />
                                <FactRow k="Grid connection" v={p.gridConnection} />
                                <FactRow k="Storage duration" v={p.duration} />
                                {p.pci && <FactRow k="EU designation" v={p.pci} />}
                            </dl>
                        </Reveal>
                    </div>
                    <div className="space-y-14 lg:col-span-7">
                        <Reveal>
                            <h2 className="eyebrow mb-5">Strategic relevance</h2>
                            <p className="body-lg">{p.strategic}</p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="eyebrow mb-5">Technical overview</h2>
                            <ol className="divide-y divide-line border-y border-line">
                                {p.technical.map((t, i) => (
                                    <li key={t} className="flex items-baseline gap-5 py-4">
                                        <span className="font-mono text-[11px] text-blue">0{i + 1}</span>
                                        <span className="font-display text-base font-medium text-ink sm:text-lg">{t}</span>
                                    </li>
                                ))}
                            </ol>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <h2 className="eyebrow mb-5">Development status</h2>
                            <p className="body-lg">
                                The project is currently in development. All capacity figures are
                                target figures and remain subject to final investment decision.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="border-y border-line bg-white">
                <div className="container-x py-16 sm:py-20">
                    <ImageBlock src={p.image} alt={`${p.tech} infrastructure — representative visual for ${p.name}`} caption="Representative infrastructure visual — project photography to follow" ratio="aspect-[21/9]" />
                </div>
            </section>

            <section id="more-projects" className="bg-paper">
                <div className="container-x py-16 sm:py-20">
                    <Reveal><h2 className="eyebrow mb-8">Other developments</h2></Reveal>
                    <ul className="grid gap-px overflow-hidden border border-line bg-line sm:grid-cols-3">
                        {others.map((o) => (
                            <li key={o.id} className="bg-white">
                                <Link to={`/projects/${o.slug}`} className="group flex h-full flex-col justify-between gap-10 p-8 transition-colors hover:bg-mist" data-testid={`other-project-${o.id}`}>
                                    <div>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-3">{o.market} · {o.tech}</p>
                                        <p className="mt-4 font-display text-xl font-bold text-ink transition-colors group-hover:text-blue">{o.name}</p>
                                    </div>
                                    <span aria-hidden="true" className="font-mono text-sm text-ink-3 transition-transform group-hover:translate-x-1">→</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>
            <CtaBand />
        </>
    );
}
